// Shared pieces for the ERP screens: who may do what, loading data, register look-ups, the common
// form inputs (select, date, file attachments) and the alert list.
import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import api, { formatApiErrorDetail, API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { kes } from "@/lib/money";
import { Pill } from "@/components/kit";

export const input = "min-h-[40px] w-full rounded-md border border-slate-300 bg-white px-2 text-sm text-slate-800";

/** Holds any of these permissions. */
export function useCan() {
  const { user } = useAuth();
  return (...perms) => perms.some((p) => (user?.perms || []).includes(p));
}

/** GET a URL (re-run when params change); { data, reload, error }. */
export function useLoad(url, params) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const key = JSON.stringify(params || {});
  const reload = useCallback(async () => {
    if (!url) return;
    try {
      const r = await api.get(url, { params: JSON.parse(key) });
      setData(r.data);
      setError(null);
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail));
      setData((d) => d ?? []);
    }
  }, [url, key]);
  useEffect(() => { reload(); }, [reload]);
  return { data, reload, error };
}

/** Run an action: a toast for success or the server's reason for refusing; returns the response data. */
export async function act(fn, ok) {
  try {
    const r = await fn();
    if (ok) toast.success(typeof ok === "function" ? ok(r?.data) : ok);
    return r?.data ?? true;
  } catch (e) {
    toast.error(formatApiErrorDetail(e.response?.data?.detail));
    return null;
  }
}

/** The registers a form needs, by kind: { trucks: [...], drivers: [...], ... } (active ones). */
export function useRegisters(kinds) {
  const [regs, setRegs] = useState({});
  const key = kinds.join(",");
  useEffect(() => {
    let live = true;
    Promise.all(key.split(",").map((k) => api.get(`/fleet/${k}`, { params: { include_inactive: false } }).then((r) => [k, r.data]).catch(() => [k, []])))
      .then((pairs) => { if (live) setRegs(Object.fromEntries(pairs)); });
    return () => { live = false; };
  }, [key]);
  return regs;
}

export function Select({ value, onChange, options, empty = "—", label, className = "", disabled }) {
  return (
    <select aria-label={label} disabled={disabled} className={`${input} disabled:bg-slate-50 ${className}`} value={value ?? ""} onChange={(e) => onChange(e.target.value || null)}>
      {empty !== null && <option value="">{empty}</option>}
      {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
    </select>
  );
}

export const opts = (rows, label = "name") => (rows || []).map((r) => ({ value: r.id, label: typeof label === "function" ? label(r) : r[label] }));

export function DateIn({ value, onChange, label }) {
  return <input type="date" aria-label={label} className={input} value={value || ""} onChange={(e) => onChange(e.target.value)} />;
}

export function Num({ value, onChange, label, placeholder }) {
  return <input inputMode="decimal" aria-label={label} placeholder={placeholder} className={`${input} text-right tabular-nums`} value={value ?? ""}
    onChange={(e) => onChange(e.target.value)} />;
}
export const n = (v) => (v === "" || v == null ? null : Number(String(v).replace(/,/g, "")));

/** Attach photos / PDFs: uploads each and keeps their ids. The camera opens on a phone. */
export function Attach({ value, onChange, label = "Photo or receipt" }) {
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);
  const add = async (files) => {
    setBusy(true);
    const ids = [...(value || [])];
    for (const f of files) {
      const fd = new FormData();
      fd.append("file", f);
      const r = await act(() => api.post("/files", fd));
      if (r?.id) ids.push(r.id);
    }
    onChange(ids);
    setBusy(false);
  };
  return (
    <div className="flex flex-wrap items-center gap-2">
      <input ref={ref} type="file" accept="image/*,application/pdf" capture="environment" multiple className="hidden"
        onChange={(e) => { add([...e.target.files]); e.target.value = ""; }} />
      <button type="button" className="min-h-[40px] rounded-md border border-dashed border-slate-400 px-3 text-sm text-slate-700 hover:bg-slate-50"
        onClick={() => ref.current?.click()} disabled={busy}>{busy ? "Uploading…" : `+ ${label}`}</button>
      {(value || []).map((id, i) => (
        <span key={id} className="inline-flex items-center gap-1 rounded bg-slate-100 px-2 py-1 text-xs">
          <a href={`${API}/files/${id}`} target="_blank" rel="noreferrer" className="text-sky-700 hover:underline">Attachment {i + 1}</a>
          <button type="button" aria-label="Remove" className="text-slate-400 hover:text-rose-600" onClick={() => onChange(value.filter((x) => x !== id))}>×</button>
        </span>
      ))}
    </div>
  );
}

export function Files({ ids }) {
  if (!ids?.length) return null;
  return <span className="flex flex-wrap gap-2">{ids.map((id, i) => <a key={id} href={`${API}/files/${id}`} target="_blank" rel="noreferrer" className="text-xs text-sky-700 hover:underline">Attachment {i + 1}</a>)}</span>;
}

const LEVEL = { critical: ["Critical", "red"], warning: ["Warning", "amber"], info: ["Note", "blue"] };
export function Alerts({ alerts }) {
  if (!alerts?.length) return <p className="mt-3 text-sm text-emerald-700">Nothing needs attention.</p>;
  return (
    <ul className="mt-3 space-y-2">
      {alerts.map((a, i) => {
        const [l, k] = LEVEL[a.level] || ["", "grey"];
        return <li key={i} className="flex items-start gap-2 text-sm"><Pill kind={k}>{l}</Pill><span>{a.text}</span></li>;
      })}
    </ul>
  );
}

// large figures shown compactly in tiles (134.06M), the exact figure on hover
export const big = (v) => {
  if (v == null || v === "") return "—";
  const x = Number(v), a = Math.abs(x);
  if (Number.isNaN(x)) return v;
  return a >= 1e6 ? `${(x / 1e6).toFixed(2)}M` : a >= 1e5 ? `${(x / 1e3).toFixed(0)}K` : x.toLocaleString("en-KE", { maximumFractionDigits: 0 });
};

export function Tile({ label, value, sub, tone = "", exact }) {
  return (
    <div className="min-w-0 rounded-lg border border-slate-200 bg-white p-4" title={exact != null ? `KES ${Number(exact).toLocaleString("en-KE", { maximumFractionDigits: 0 })}` : undefined}>
      <div className="text-xs text-slate-500">{label}</div>
      <div className={`mt-1 truncate text-xl font-semibold tabular-nums ${tone}`}>{value}</div>
      {sub && <div className="mt-0.5 text-xs text-slate-500">{sub}</div>}
    </div>
  );
}

export const k0 = (v) => (v == null ? "—" : Number(v).toLocaleString("en-KE", { maximumFractionDigits: 0 }));
export const pct = (v) => (v == null ? "—" : `${Number(v).toFixed(1)}%`);
export { kes };

const SPEND_STATUS = { pending: ["Waiting approval", "blue"], approved: ["Approved – to pay", "amber"], paid: ["Paid", "green"], rejected: ["Rejected", "red"],
  cancelled: ["Cancelled", "grey"], history: ["History", "grey"], issued: ["Issued", "green"], closed: ["Closed", "grey"],
  dispatched: ["On its way to load", "blue"], loaded: ["Loaded", "amber"], delivered: ["Delivered", "green"], invoiced: ["Invoiced", "green"],
  superseded: ["Replaced", "grey"] };
export function State({ s }) {
  const [l, k] = SPEND_STATUS[s] || [s, "grey"];
  return <Pill kind={k}>{l}</Pill>;
}

/** The first day of this month and today, for period pickers. */
export function thisMonth() {
  const d = new Date();
  const iso = (x) => x.toISOString().slice(0, 10);
  return { date_from: iso(new Date(d.getFullYear(), d.getMonth(), 1, 12)), date_to: iso(d) };
}

export function Period({ value, onChange }) {
  return (
    <div className="mt-4 flex flex-wrap items-end gap-3">
      <label className="flex flex-col gap-1 text-xs text-slate-500">From<DateIn value={value.date_from} onChange={(v) => onChange({ ...value, date_from: v })} /></label>
      <label className="flex flex-col gap-1 text-xs text-slate-500">To<DateIn value={value.date_to} onChange={(v) => onChange({ ...value, date_to: v })} /></label>
    </div>
  );
}
